import React from 'react';
import { observer } from '../../shared/observer';
import { runBusy, isBusy } from '../../shared/busy';
import { curFormat } from '../../shared/format';
import { minOrderCount, maxOrderCount, initialOrderCount, isOutOfStock, isFirstOrderUsed, FIRST_ORDER_USED_NOTE } from '../../shared/orderCount';
import { unitPriceForQuantity } from '../../shared/price';

// Универсальный «В корзину» виджет для карточек товара.
// Монтаж: <div data-react="ToCart" data-params='{"itemId":"...", "price":13000, ...}'>...fallback...</div>
//
// SSR-фолбэк до гидратации внутри контейнера: цена + счётчик + кнопка.
// React после гидратации заменяет содержимое на реактивный счётчик.
//
// До добавления показываем степпер с выбором количества (pending), как
// CatalogQuantitySelector в мобилке: видно, сколько добавится (default_order_count,
// но не ниже min), и можно поправить до клика «В корзину». После добавления —
// степпер правит количество в корзине напрямую.

class ToCartView extends React.Component {
  constructor(props) {
    super(props);
    // pending — выбранное количество до добавления в корзину
    this.state = { pending: initialOrderCount(props.params), busy: {}, error: null };
  }

  // Шаг степпера до добавления — в пределах [min, склад].
  incPending = () => {
    const { params } = this.props;
    this.setState((s) => ({ pending: Math.min(s.pending + 1, maxOrderCount(params)) }));
  };
  decPending = () => {
    const { params } = this.props;
    this.setState((s) => ({ pending: Math.max(s.pending - 1, minOrderCount(params)) }));
  };
  // Пейлоад для v3 carts: группа цен из data-params (блок каталога/баннера)
  // уходит на сервер вместе с количеством — иначе корзина посчитает базовую
  // цену без группы. Сервер сам перепроверяет применимость группы.
  cartPayload(quantity) {
    const { params } = this.props;
    const payload = { item_id: params.itemId, quantity };
    if (params.priceGroupId) payload.price_group_id = params.priceGroupId;
    return payload;
  }

  addPending = () => {
    const { cart } = this.props;
    if (isBusy(this, 'add')) return;
    this.setState({ error: null });
    runBusy(this, 'add', () => cart.addProductToCart(this.cartPayload(this.state.pending)))
      .catch(() => this.setState({ error: 'Не удалось добавить товар' }));
  };

  // «+» у товара, уже лежащего в корзине (потолок по складу, как в мобилке).
  inc = () => {
    const { params, cart } = this.props;
    const item = this.findItem();
    if (!item) return;
    if (item.quantity >= maxOrderCount(params) || isFirstOrderUsed(params)) return;
    cart.addProductToCart(this.cartPayload(item.quantity + 1));
  };
  dec = () => {
    const { params, cart } = this.props;
    const item = this.findItem();
    if (!item) return;
    const next = item.quantity - 1;
    // ниже минимального количества — товар убирается из корзины
    // (откат при ошибке делает стор, reject глушим от unhandled rejection)
    if (next < minOrderCount(params)) cart.removeProductFromCart(params.itemId).catch(() => {});
    else cart.addProductToCart(this.cartPayload(next));
  };

  findItem() {
    const { params, cart } = this.props;
    return (cart.items || []).find((i) => i.item_id === params.itemId);
  }

  // Текущая цена с учётом тиров. До добавления берём pending-количество,
  // чтобы цена сразу отражала выбранный объём (как unitPriceForQuantity в мобилке).
  currentPrice() {
    const item = this.findItem();
    const qty = item ? item.quantity : this.state.pending;
    return unitPriceForQuantity(this.props.params, qty);
  }

  // Подпись о складе/минимуме, как StockInfo в мобилке: «нет в наличии» при
  // отсутствии на складе, иначе «от N шт» при минимальном количестве > 1.
  stockNote() {
    const { params } = this.props;
    if (isOutOfStock(params)) return 'нет в наличии';
    const min = minOrderCount(params);
    return min > 1 ? `от ${min} шт` : '';
  }

  render() {
    const { auth } = this.props;
    const item = this.findItem();
    const currency = (auth.companyInfo.currency_hash && auth.companyInfo.currency_hash.symbol_html) || '';
    const priceStr = `${curFormat(this.currentPrice())} ${currency}`;
    const note = this.stockNote();

    if (item) {
      // «+» гаснет на потолке склада — иначе клик выглядит рабочим, но молча ничего не делает
      const atMax = item.quantity >= maxOrderCount(this.props.params) || isFirstOrderUsed(this.props.params);
      return (
        <div className="ng-to-cart ng-to-cart--in-cart">
          <div className="ng-to-cart__row">
            <span>{priceStr}</span>
            <span className="item-show-price-incart__wrapper">
              <a href="#" className="down" onClick={(e) => { e.preventDefault(); this.dec(); }}>−</a>
              <span className="item-show-price-incart__count">{item.quantity}</span>
              <a href="#" className={'up' + (atMax ? ' is-disabled' : '')} aria-disabled={atMax ? 'true' : null} onClick={(e) => { e.preventDefault(); this.inc(); }}>+</a>
            </span>
          </div>
          <a href="/cart" className="ng-to-cart__checkout">
            К оформлению
            <span className="ng-to-cart__arrow" aria-hidden="true">→</span>
          </a>
          {isFirstOrderUsed(this.props.params) && <div className="ng-field-error">{FIRST_ORDER_USED_NOTE}</div>}
        </div>
      );
    }
    if (isFirstOrderUsed(this.props.params)) {
      return (
        <div className="ng-to-cart ng-to-cart--in-cart">
          <div className="ng-to-cart__row"><span>{priceStr}</span></div>
          <div className="ng-field-error">{FIRST_ORDER_USED_NOTE}</div>
        </div>
      );
    }
    const atMaxPending = this.state.pending >= maxOrderCount(this.props.params);
    const adding = isBusy(this, 'add');
    return (
      <div className="ng-to-cart ng-to-cart--in-cart">
        <div className="ng-to-cart__row">
          <span>{priceStr}</span>
          <span className="item-show-price-incart__wrapper">
            <a href="#" className="down" onClick={(e) => { e.preventDefault(); this.decPending(); }}>−</a>
            <span className="item-show-price-incart__count">{this.state.pending}</span>
            <a href="#" className={'up' + (atMaxPending ? ' is-disabled' : '')} aria-disabled={atMaxPending ? 'true' : null} onClick={(e) => { e.preventDefault(); this.incPending(); }}>+</a>
          </span>
        </div>
        {note ? <span className="ng-to-cart__note">{note}</span> : null}
        <button
          type="button"
          className={'btn btn--mini btn--block' + (adding ? ' is-busy' : '')}
          disabled={adding}
          onClick={this.addPending}
        >В корзину</button>
        {this.state.error ? <div className="order-message">{this.state.error}</div> : null}
      </div>
    );
  }
}

export const ToCart = observer(ToCartView, ['cartStore', 'authStore']);



// WEBPACK FOOTER //
// ./app/javascript/website_newgen/js/components/Cart/ToCart.jsx